/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRedirect, useNotify } from 'react-admin';
import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import type { Genre } from '@musedrops/shared';
import { logger } from '@musedrops/shared';
import { getGenreInstructions } from '../../../data/genre';
import { FormTextField } from '../../../shared/components/forms';
import { GenreSelector } from '../components/GenreSelector';
import { LanguageSelector } from '../../../shared/components/LanguageSelector';
import { useUnsavedChanges } from '../../../shared/hooks';
import { useShowIdeas } from '../../../hooks/useShowIdeas';
import { useQueueJob } from '../../../hooks/useQueueJob';
import { GenreApi } from '../../../services/genreApi';
import { getEdgeFunctionAuthHeaders } from '../../../services/supabase';
import { LAYOUT } from '../../../theme';

export const ShowCreate = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [topic, setTopic] = useState('');
  const [genreId, setGenreId] = useState<string>('');
  const [lang, setLang] = useState<string | null>('en'); // Language defaults to English, null = all languages
  const [ideasCount, setIdeasCount] = useState<number>(5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [genres, setGenres] = useState<Genre[]>([]);
  // Track generation state per idea: { [ideaTopic]: { generating: boolean, showId?: string } }
  const [ideaStates, setIdeaStates] = useState<
    Record<string, { generating: boolean; showId?: string }>
  >({});
  // Track which idea key is currently being processed (for linking job completion to idea)
  const pendingIdeaKeyRef = useRef<string | null>(null);
  // Track if manual tab generation is in progress (vs idea click)
  const isManualGenerationRef = useRef(false);
  const redirect = useRedirect();
  const notify = useNotify();
  const manualSectionRef = useRef<HTMLDivElement>(null);

  // Load genres for mapping genre names to IDs
  useEffect(() => {
    const loadGenres = async () => {
      try {
        const genreList = await GenreApi.getAll();
        setGenres(genreList);
      } catch (err) {
        logger.error('Failed to load genres:', err);
      }
    };
    loadGenres();
  }, []);

  // Use shared unsaved changes hook
  const { handleCancel: handleUnsavedCancel } = useUnsavedChanges({
    dependencies: [topic, genreId, lang, ideasCount],
    onCancel: () => redirect('list', 'shows'),
  });

  // Use show ideas hook for the ideas section
  const {
    loading: ideasLoading,
    ideas,
    error: ideasError,
    generateIdeas,
    clearError: clearIdeasError,
  } = useShowIdeas();

  // Handle show profile job completion
  const handleProfileJobComplete = useCallback(
    (output: Record<string, unknown>) => {
      const showId = output?.showId as string | undefined;
      const showSlug = output?.showSlug as string | undefined;
      const ideaKey = pendingIdeaKeyRef.current;
      const isManual = isManualGenerationRef.current;

      logger.info('Show profile job completed', {
        showId,
        showSlug,
        ideaKey,
        isManual,
      });

      if (showId) {
        notify('Show created successfully!');

        if (isManual) {
          // Manual tab: redirect to show edit page
          redirect('edit', 'shows', showId);
        } else if (ideaKey) {
          // Idea click: update idea state with showId
          setIdeaStates(prev => ({
            ...prev,
            [ideaKey]: { generating: false, showId },
          }));
        }
      } else {
        // Job completed but no showId returned
        notify('Show profile generated, but no show ID returned', {
          type: 'warning',
        });
        if (isManual) {
          redirect('list', 'shows');
        } else if (ideaKey) {
          setIdeaStates(prev => ({
            ...prev,
            [ideaKey]: { generating: false },
          }));
        }
      }

      // Reset state
      setIsGenerating(false);
      pendingIdeaKeyRef.current = null;
      isManualGenerationRef.current = false;
    },
    [notify, redirect]
  );

  // Handle show profile job error
  const handleProfileJobError = useCallback(
    (errorMsg: string) => {
      const ideaKey = pendingIdeaKeyRef.current;
      const isManual = isManualGenerationRef.current;

      logger.error('Show profile job failed', {
        error: errorMsg,
        ideaKey,
        isManual,
      });
      notify(`Show generation failed: ${errorMsg}`, { type: 'error' });

      if (ideaKey) {
        setIdeaStates(prev => ({
          ...prev,
          [ideaKey]: { generating: false },
        }));
      }

      setIsGenerating(false);
      pendingIdeaKeyRef.current = null;
      isManualGenerationRef.current = false;
    },
    [notify]
  );

  // Handle show profile job timeout
  const handleProfileJobTimeout = useCallback(() => {
    const ideaKey = pendingIdeaKeyRef.current;
    const isManual = isManualGenerationRef.current;

    logger.warn('Show profile job timed out', { ideaKey, isManual });
    notify('Show generation timed out. Please try again.', { type: 'error' });

    if (ideaKey) {
      setIdeaStates(prev => ({
        ...prev,
        [ideaKey]: { generating: false },
      }));
    }

    setIsGenerating(false);
    pendingIdeaKeyRef.current = null;
    isManualGenerationRef.current = false;
  }, [notify]);

  // Use queue job hook for show profile generation
  const { watchJob: watchProfileJob, stopWatching: stopWatchingProfile } =
    useQueueJob({
      timeout: 2 * 60 * 1000, // 2 minutes for show profile generation
      onComplete: handleProfileJobComplete,
      onError: handleProfileJobError,
      onTimeout: handleProfileJobTimeout,
    });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopWatchingProfile();
    };
  }, [stopWatchingProfile]);

  const handleGenerateIdeas = async () => {
    const selectedGenre = genres.find(g => g.id === genreId);
    if (!selectedGenre) {
      notify('Please select a genre before generating ideas.', {
        type: 'warning',
      });
      return;
    }

    const validCount =
      Number.isFinite(ideasCount) && ideasCount > 0
        ? Math.floor(ideasCount)
        : 1;
    setIdeasCount(validCount);

    await generateIdeas({
      genreId: selectedGenre.id,
      genreName: selectedGenre.name,
      genreDescription: selectedGenre.description || '',
      lang, // Pass language to idea generation
      ideasCount: validCount,
    });
  };

  /**
   * Generate show profile using queue-centric architecture.
   * Creates a queue job and watches for completion via useQueueJob hook.
   */
  const generateContent = async (
    topicOverride?: string,
    genreIdOverride?: string,
    ideaKey?: string
  ) => {
    const topicToUse = topicOverride || topic;
    const genreIdToUse = genreIdOverride || genreId;

    // Ensure topicToUse is a string before calling trim
    if (!topicToUse || typeof topicToUse !== 'string' || !topicToUse.trim()) {
      return;
    }

    // Track context for job completion callbacks
    pendingIdeaKeyRef.current = ideaKey || null;
    isManualGenerationRef.current = !ideaKey;

    // For manual creation, use isGenerating flag
    if (!ideaKey) {
      setIsGenerating(true);
    }

    try {
      // Get genre instructions if a genre is selected
      let genreInstructions = '';
      if (genreIdToUse) {
        try {
          genreInstructions = await getGenreInstructions(genreIdToUse);
        } catch (genreError) {
          logger.warn('Failed to load genre instructions:', genreError);
        }
      }

      // Call gen-show-profile function - returns jobId, not showId
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gen-show-profile`,
        {
          method: 'POST',
          headers: await getEdgeFunctionAuthHeaders(),
          body: JSON.stringify({
            operation: 'show-profile',
            'show-topic': topicToUse,
            'genre-id': genreIdToUse,
            'genre-instructions': genreInstructions,
            lang, // Pass language to profile generation
            trigger: 'manual', // UI-initiated jobs get priority
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      // Queue-centric: response contains jobId, not showId
      const jobId = data.result?.jobId;
      if (!jobId) {
        throw new Error('No job ID returned from edge function');
      }

      logger.info('Show profile job created, watching for completion', {
        jobId,
        topicToUse,
        ideaKey,
      });

      // Start watching the job for completion
      // The callbacks (handleProfileJobComplete, etc.) will handle the rest
      watchProfileJob(jobId);
    } catch (error) {
      // Clear generating state on error
      if (ideaKey) {
        setIdeaStates(prev => ({
          ...prev,
          [ideaKey]: { generating: false },
        }));
      }
      notify(
        error instanceof Error
          ? error.message
          : 'Failed to start show generation. Please try again.',
        { type: 'error' }
      );
      setIsGenerating(false);
      pendingIdeaKeyRef.current = null;
      isManualGenerationRef.current = false;
    }
    // Note: No finally block that resets isGenerating - the callbacks handle that
  };

  // Handle clicking an idea to generate show immediately
  const handleIdeaClick = async (topic: string, genreName?: string) => {
    const ideaKey = `${topic}-${genreName || genreId}`;

    // Don't allow clicking if already generating or complete
    if (ideaStates[ideaKey]?.generating || ideaStates[ideaKey]?.showId) {
      return;
    }

    let genreIdToUse = genreId; // Use currently selected genre as fallback

    // If idea has a genre name, try to find the matching genre ID
    if (genreName) {
      const matchedGenre = genres.find(
        g => g.name.toLowerCase() === genreName.toLowerCase()
      );
      if (matchedGenre) {
        genreIdToUse = matchedGenre.id;
      }
    }

    // Must have a genre to generate
    if (!genreIdToUse) {
      notify('Please select a genre before generating.', { type: 'warning' });
      return;
    }

    // Mark this idea as generating
    setIdeaStates(prev => ({
      ...prev,
      [ideaKey]: { generating: true },
    }));

    // Start generating immediately with both topic and genre
    await generateContent(topic, genreIdToUse, ideaKey);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: LAYOUT.narrowContentWidth,
        mx: 'auto',
        pt: 2,
        px: 2,
        pb: '500px',
      }}
    >
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={handleUnsavedCancel} sx={{ p: 1 }}>
          <ArrowLeft size={20} />
        </IconButton>
        <Typography variant="h5" component="h1">
          Add a Show
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Generate" />
          <Tab label="Manual" />
        </Tabs>
      </Box>

      {/* Tab 1: Generate Ideas */}
      {activeTab === 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <GenreSelector
              selectedGenreId={genreId}
              onGenreChange={setGenreId}
              width="medium"
            />

            <FormTextField
              label="Number of show ideas"
              type="number"
              value={ideasCount}
              onChange={e => setIdeasCount(Number(e.target.value))}
              placeholder="e.g., 5"
              inputProps={{ min: 1, max: 20 }}
              width="small"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
            <Button
              variant="outlined"
              onClick={handleGenerateIdeas}
              disabled={
                ideasLoading ||
                isGenerating ||
                !genreId ||
                !ideasCount ||
                ideasCount < 1
              }
              startIcon={ideasLoading ? <CircularProgress size={20} /> : null}
            >
              {ideasLoading ? 'Generating Ideas...' : 'Generate Ideas'}
            </Button>
          </Box>

          {ideasError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={clearIdeasError}>
              {ideasError}
            </Alert>
          )}

          {ideas.length > 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Generated Ideas (click to create show)
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <LanguageSelector
                    value={lang}
                    onChange={setLang}
                    label="Language"
                    width="small"
                    showAllOption
                    allOptionLabel="All global languages"
                  />
                </Box>
                <List>
                  {ideas.map((idea, index) => {
                    const ideaKey = `${idea.topic}-${idea.genre || genreId}`;
                    const state = ideaStates[ideaKey];
                    const isGenerating = state?.generating || false;
                    const isComplete = !!state?.showId;

                    const displayText = idea.genre ? (
                      <>
                        <strong>[{idea.genre}]:</strong> {idea.topic}
                      </>
                    ) : (
                      idea.topic
                    );

                    return (
                      <Box
                        key={`idea-${index}-${idea.topic.slice(0, 20).replace(/[^a-zA-Z0-9]/g, '')}`}
                      >
                        <ListItem
                          disablePadding
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                          }}
                        >
                          <ListItemButton
                            onClick={() =>
                              handleIdeaClick(idea.topic, idea.genre)
                            }
                            disabled={isGenerating}
                            sx={{
                              borderRadius: 1,
                              pr: 1,
                              '&:hover': {
                                backgroundColor: isComplete
                                  ? 'transparent'
                                  : 'action.hover',
                              },
                              cursor: isComplete ? 'default' : 'pointer',
                              '&.Mui-disabled': {
                                opacity: 1,
                              },
                            }}
                          >
                            <Box sx={{ width: '100%' }}>
                              <ListItemText
                                primary={displayText}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  sx: { lineHeight: 1.4 },
                                }}
                              />
                              {isGenerating && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mt: 1,
                                  }}
                                >
                                  <CircularProgress size={16} />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Generating...
                                  </Typography>
                                </Box>
                              )}
                              {isComplete && state.showId && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mt: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'success.main' }}
                                  >
                                    ✓
                                  </Typography>
                                  <Link
                                    href={`#/shows/${state.showId}`}
                                    onClick={e => {
                                      // Allow Ctrl/Cmd/middle-click to open in new tab
                                      if (
                                        e.ctrlKey ||
                                        e.metaKey ||
                                        e.button === 1
                                      ) {
                                        return;
                                      }
                                      e.preventDefault();
                                      e.stopPropagation();
                                      redirect('edit', 'shows', state.showId!);
                                    }}
                                    sx={{
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    View show
                                  </Link>
                                </Box>
                              )}
                            </Box>
                          </ListItemButton>
                        </ListItem>
                        {index < ideas.length - 1 && <Divider />}
                      </Box>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Tab 2: Manual Input Form */}
      {activeTab === 1 && (
        <Box ref={manualSectionRef}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormTextField
              label="What is the show about?"
              value={topic}
              onChange={e => setTopic(e.target.value)}
              placeholder="e.g., Emotional stories from history"
              width="full"
              required
              onKeyDown={e => {
                if (e.key === 'Enter' && topic.trim()) {
                  generateContent();
                }
              }}
            />

            <GenreSelector
              selectedGenreId={genreId}
              onGenreChange={setGenreId}
              width="medium"
            />

            <LanguageSelector
              value={lang}
              onChange={setLang}
              label="Language"
              width="small"
              showAllOption
              allOptionLabel="All global languages"
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="contained"
                onClick={() => generateContent()}
                disabled={!topic.trim() || isGenerating || ideasLoading}
                startIcon={isGenerating ? <CircularProgress size={20} /> : null}
              >
                {isGenerating ? 'Generating...' : 'Generate Show'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
